import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import pollingSlice, {
  closePolls,
  getAnswersOfSpecificPoll,
  getOtherActivePolls,
  getPollById,
  getQuestionsByPollId,
  setPollQuestionActiveOrInActive,
  updatePollStatus,
  getPollAssociatedDataAlongSpeaker,
  updatePollById
} from 'store/polling'
import { Box } from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  PlayArrow,
  Stop,
  Pause,
  Fullscreen,
  FullscreenExit,
  Poll as PollIcon,
  Event as EventIcon,
  Person as PersonIcon,
  SearchOff,
  CheckCircle
} from '@material-ui/icons'
import PollQuestion from 'components/PollQuestion'
import { PollStatus } from 'constants/index'
import { colors } from 'utils/colors'
import { useParams } from 'react-router-dom'
import PresentationMode from 'views/admin/polling/public/PresentationMode'
import ProgressButton from 'components/ProgressButton'
import { cloudFunctionsInstance } from 'config/axios'

const PollView = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { pollId } = useParams()

  const fullScreenRef = useRef(null)

  const [isPollStarted, setIsPollStarted] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isMovingQuestion, setIsMovingQuestion] = useState(false)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [presentationMode, setPresentationMode] = useState(false)
  const [otherActivePolls, setOtherActivePolls] = useState([])
  const [openMultiPollWarning, setOpenMultiPollWarning] = useState(false)

  const { inProgress, poll, pollAssociatedData, questions } = useSelector((state) => state.polling)
  const isPollLive =
    poll?.id && poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED

  // Warn before any departure (refresh, tab close, navigation) - browsers don't expose which one
  // it is, so this is the only way to let the presenter back out of accidentally ending the poll.
  const handleBeforeUnload = (event) => {
    if (isPollLive) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  // pagehide only fires once the departure is confirmed (or there was nothing to confirm), so the
  // beacon never fires if the presenter cancels out of the beforeunload prompt above. `persisted`
  // guards against bfcache: that's a page going dormant for back/forward, not actually leaving.
  const handlePageHide = (event) => {
    if (!event.persisted && isPollLive) {
      const currentQuestionId = questions[questionIndex]?.id
      navigator.sendBeacon(
        `${cloudFunctionsInstance.defaults.baseURL}/endPollOnWindowClose`,
        JSON.stringify({ pollId: pollId, questionId: currentQuestionId })
      )
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [poll?.id, poll?.status])

  const handleOpenFullScreen = () => {
    if (!presentationMode) {
      setPresentationMode(true)
      if (fullScreenRef.current.requestFullscreen) {
        fullScreenRef.current.requestFullscreen()
      } else if (fullScreenRef.current.mozRequestFullScreen) {
        fullScreenRef.current.mozRequestFullScreen()
      } else if (fullScreenRef.current.webkitRequestFullscreen) {
        fullScreenRef.current.webkitRequestFullscreen()
      } else if (fullScreenRef.current.msRequestFullscreen) {
        fullScreenRef.current.msRequestFullscreen()
      }
    }
  }

  const handleCloseFullScreen = () => {
    if (presentationMode) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      setPresentationMode(false)
    }
  }
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setPresentationMode(false)
      handleCloseFullScreen()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(true)
  }

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }

  const handleConfirmModal = async () => {
    setIsUpdating(true)
    const currentQuestionId = questions[questionIndex]?.id
    await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
    await dispatch(updatePollById(poll?.id, { status: PollStatus.CLOSED, isActive: false }))
    handleCloseConfirmModal()
    setIsUpdating(false)
    handleCloseFullScreen()
    window.close()
  }

  const activeQuestionIndex =
    poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED
      ? questions?.findIndex((question) => question?.isActive === true)
      : -1

  useEffect(() => {
    if (activeQuestionIndex > -1) {
      setQuestionIndex(activeQuestionIndex)
    }
  }, [activeQuestionIndex])

  // isPollStarted only exists to avoid a flicker between clicking "Start" and the Firestore
  // write landing back in `poll` — it should never outlive the poll actually being CREATED
  // again. Without this, resetting the poll (e.g. via "Reset Poll" in PollingForm.js) while
  // this page stays open flips `poll.status` back to CREATED in real time, but the stale
  // local flag keeps hiding the "ready to start" screen, leaving the raw (now-empty)
  // question view showing through instead.
  useEffect(() => {
    if (poll?.status === PollStatus.CREATED) {
      setIsPollStarted(false)
      setQuestionIndex(0)
    }
  }, [poll?.status])

  useEffect(() => {
    const fetchAssociatedData = async () => {
      setIsFetching(true)
      await dispatch(
        getPollAssociatedDataAlongSpeaker(
          poll?.talkId,
          poll?.sessionId,
          poll?.eventId,
          poll?.speakerId
        )
      )
      setIsFetching(false)
    }

    fetchAssociatedData()
  }, [poll?.id])

  const updatePoll = async (status) => {
    setIsUpdating(true)
    await dispatch(updatePollStatus(poll?.id, status))
    setIsUpdating(false)
  }

  const handleQuestionBack = async () => {
    setIsMovingQuestion(true)
    if (poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED) {
      const currentQuestionId = questions[questionIndex]?.id
      const prevQuestionId = questions[questionIndex - 1]?.id
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, prevQuestionId, true))
    }
    setIsMovingQuestion(false)
    setQuestionIndex(questionIndex - 1)
  }

  const handleQuestionNext = async () => {
    setIsMovingQuestion(true)
    if (poll?.status !== PollStatus.CREATED && poll?.status !== PollStatus.CLOSED) {
      const currentQuestionId = questions[questionIndex]?.id
      const nextQuestionId = questions[questionIndex + 1]?.id
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, false))
      await dispatch(setPollQuestionActiveOrInActive(poll?.id, nextQuestionId, true))
    }
    setIsMovingQuestion(false)
    setQuestionIndex(questionIndex + 1)
  }

  useEffect(() => {
    if (pollId) {
      dispatch(pollingSlice.actions.setProgress(true))
      const unsubPoll = getPollById(pollId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      const unsub = getQuestionsByPollId(pollId, (data) => {
        dispatch(pollingSlice.actions.setQuestions(data))
      })
      const unsubscribeAnswers = getAnswersOfSpecificPoll(pollId, (data) => {
        dispatch(pollingSlice.actions.setAnswers(data))
      })
      return () => {
        unsubPoll && unsubPoll()
        unsub && unsub()
        unsubscribeAnswers && unsubscribeAnswers()
      }
    }
  }, [dispatch, pollId])

  const startPoll = async () => {
    handleOpenFullScreen()
    setIsUpdating(true)
    const currentQuestionId = questions[questionIndex]?.id
    await dispatch(setPollQuestionActiveOrInActive(poll?.id, currentQuestionId, true))
    const payload = {
      isActive: true,
      status: PollStatus.STARTED
    }
    await dispatch(updatePollById(pollId, payload))
    setIsPollStarted(true)
    setIsUpdating(false)
  }

  // Only one poll can be live per event/session/talk at a time — check for siblings still
  // running before starting this one, and warn the presenter that they'll be closed.
  const handleStartPoll = async () => {
    setIsUpdating(true)
    const others = await getOtherActivePolls(poll)
    setIsUpdating(false)
    if (others.length) {
      setOtherActivePolls(others)
      setOpenMultiPollWarning(true)
      return
    }
    await startPoll()
  }

  const handleCloseMultiPollWarning = () => {
    setOpenMultiPollWarning(false)
    setOtherActivePolls([])
  }

  const handleConfirmMultiPollWarning = async () => {
    setIsUpdating(true)
    await closePolls(otherActivePolls.map((otherPoll) => otherPoll.id))
    setIsUpdating(false)
    handleCloseMultiPollWarning()
    await startPoll()
  }

  if (inProgress) {
    return (
      <Box className={classes.statusPage}>
        <CircularProgress style={{ color: colors.indigo }} />
      </Box>
    )
  } else if (!poll?.id) {
    return (
      <Box className={classes.statusPage}>
        <Box className={classes.statusCard}>
          <Box className={classes.statusIconWrap}>
            <SearchOff className={classes.statusIcon} />
          </Box>
          <Typography className={classes.statusHeader}>Poll not found</Typography>
          <Typography className={classes.statusSubHeader}>
            It looks like the poll you&apos;re looking for doesn&apos;t exist. Please verify the URL
            and try again.
          </Typography>
        </Box>
      </Box>
    )
  } else if (poll?.status === PollStatus.CLOSED) {
    // No `!presentationMode` guard here on purpose: a poll can also close because another
    // poll for the same event/session/talk was just started elsewhere (see
    // getOtherActivePolls/closePolls), which happens with no local fullscreen-exit trigger —
    // without this, the presenter would be stuck looking at the last question with every
    // control disabled instead of being told the poll ended.
    return (
      <Box className={classes.statusPage}>
        <Box className={classes.statusCard}>
          <Box className={classes.statusIconWrap}>
            <CheckCircle className={classes.statusIcon} />
          </Box>
          <Typography className={classes.statusHeader}>This poll has ended</Typography>
          <Typography className={classes.statusSubHeader}>
            Please verify the URL and try again.
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      ref={fullScreenRef}
      sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tooltip
        title={
          <Typography>{presentationMode ? 'Exit Full Screen' : 'Enter Full Screen'}</Typography>
        }>
        <IconButton
          className={classes.fullscreenToggle}
          onClick={presentationMode ? handleCloseFullScreen : handleOpenFullScreen}>
          {presentationMode ? <FullscreenExit fontSize="large" /> : <Fullscreen fontSize="large" />}
        </IconButton>
      </Tooltip>
      <PresentationMode>
        {poll?.status === PollStatus.CREATED && !isPollStarted && (
          <Box
            className={classes.backdrop}
            style={{ backdropFilter: 'blur(2px)', zIndex: '99999' }}>
            <Box className={classes.backdropContent}>
              <Box className={classes.readyCard}>
                {isFetching ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '1rem',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '3rem 0'
                    }}>
                    <CircularProgress size={32} /> Loading...
                  </Box>
                ) : (
                  <Box className={classes.readyDetails}>
                    <Box className={classes.readyRow}>
                      <Box className={classes.readyIconWrap}>
                        <PollIcon />
                      </Box>
                      <Box>
                        <Typography className={classes.readyLabel}>Poll Title</Typography>
                        <Typography className={classes.readyValue}>
                          {poll?.title ?? 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.readyRow}>
                      <Box className={classes.readyIconWrap}>
                        <EventIcon />
                      </Box>
                      <Box>
                        <Typography className={classes.readyLabel}>
                          {pollAssociatedData?.type ?? 'Event'}
                        </Typography>
                        <Typography className={classes.readyValue}>
                          {pollAssociatedData?.title ?? 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.readyRow}>
                      <Box className={classes.readyIconWrap}>
                        <PersonIcon />
                      </Box>
                      <Box>
                        <Typography className={classes.readyLabel}>Presenter</Typography>
                        <Typography className={classes.readyValue}>
                          {pollAssociatedData?.presenter ?? 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                <Box className={classes.readyActions}>
                  <div
                    className={isUpdating || isFetching ? classes.disabledStart : classes.start}
                    onClick={handleStartPoll}>
                    {isUpdating ? (
                      'Starting...'
                    ) : (
                      <>
                        <PlayArrow fontSize="large" className={classes.startIcon} />
                        Start
                      </>
                    )}
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        <PollQuestion question={questions[questionIndex]} />
        {openConfirmModal && (
          <Box
            className={classes.backdrop}
            style={{ backdropFilter: 'blur(2px)', zIndex: '99999' }}>
            <Box className={classes.backdropContent}>
              <div className={classes.warningCard}>
                <p style={{ fontSize: '18px', fontWeight: 'bold' }}>End Poll</p>
                <p style={{ fontSize: '16px', marginRight: '4rem' }}>
                  Do you really want to end this poll?
                </p>
                <Box className={classes.btnBox}>
                  <Button color="inherit" onClick={handleCloseConfirmModal} disabled={isUpdating}>
                    No
                  </Button>
                  <ProgressButton
                    label="yes"
                    inProgress={isUpdating}
                    disabled={isUpdating}
                    onClick={handleConfirmModal}
                  />
                </Box>
              </div>
            </Box>
          </Box>
        )}
        {openMultiPollWarning && (
          <Box
            className={classes.backdrop}
            style={{ backdropFilter: 'blur(2px)', zIndex: '99999' }}>
            <Box className={classes.backdropContent}>
              <div className={classes.warningCard}>
                <p style={{ fontSize: '18px', fontWeight: 'bold' }}>Other polls are active</p>
                <p style={{ fontSize: '16px' }}>
                  Starting this poll will close the following active poll
                  {otherActivePolls.length > 1 ? 's' : ''} for this event/session/talk:
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '16px' }}>
                  {otherActivePolls.map((otherPoll) => (
                    <li key={otherPoll.id}>{otherPoll.title ?? 'Untitled poll'}</li>
                  ))}
                </ul>
                <p style={{ fontSize: '16px' }}>Do you want to continue?</p>
                <Box className={classes.btnBox}>
                  <Button
                    color="inherit"
                    onClick={handleCloseMultiPollWarning}
                    disabled={isUpdating}>
                    No
                  </Button>
                  <ProgressButton
                    label="yes"
                    inProgress={isUpdating}
                    disabled={isUpdating}
                    onClick={handleConfirmMultiPollWarning}
                  />
                </Box>
              </div>
            </Box>
          </Box>
        )}
      </PresentationMode>
      {poll?.status !== PollStatus.CREATED && (
        <Box className={classes.bottomBarWrapper}>
          <Box className={classes.bottomBar}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Tooltip title={<Typography>Previous</Typography>}>
                <Button
                  variant="outlined"
                  className={classes.navBtn}
                  disabled={!questionIndex > 0 || isMovingQuestion}
                  onClick={handleQuestionBack}>
                  <ChevronLeft fontSize="large" className={classes.arrow} />
                </Button>
              </Tooltip>
              <Typography className={classes.questionIndex}>
                {questionIndex + 1}/{questions.length}
              </Typography>
              <Tooltip title={<Typography>Next</Typography>}>
                <Button
                  variant="outlined"
                  className={classes.navBtn}
                  disabled={questionIndex === questions?.length - 1 || isMovingQuestion}
                  onClick={handleQuestionNext}>
                  <ChevronRight fontSize="large" className={classes.arrow} />
                </Button>
              </Tooltip>
            </Box>
            <Box className={classes.actionBtns}>
              <Button
                size="large"
                startIcon={poll?.status === PollStatus.PAUSED ? <PlayArrow /> : <Pause />}
                variant="contained"
                color={poll?.status === PollStatus.PAUSED ? 'primary' : 'inherit'}
                className={poll?.status === PollStatus.PAUSED ? classes.startBtn : classes.pauseBtn}
                disabled={
                  isUpdating ||
                  poll?.status === PollStatus.CREATED ||
                  poll?.status === PollStatus.CLOSED
                }
                onClick={() =>
                  updatePoll(
                    poll?.status === PollStatus.PAUSED ? PollStatus.RESUMED : PollStatus.PAUSED
                  )
                }>
                {poll?.status === PollStatus.PAUSED ? 'Resume Poll' : 'Pause Poll'}
              </Button>
              <Button
                size="large"
                className={classes.errorBtn}
                startIcon={<Stop />}
                color="error"
                variant="contained"
                disabled={poll?.status === PollStatus.CLOSED}
                onClick={handleOpenConfirmModal}>
                End Poll
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default PollView

const commonStart = {
  color: '#FFFFFF',
  textTransform: 'none',
  width: '240px',
  fontSize: '26px',
  fontWeight: '600',
  letterSpacing: '1.25px',
  borderRadius: '12px',
  borderColor: colors.indigo,
  display: 'flex',
  justifyContent: 'center',
  gap: '4px',
  alignItems: 'center',
  padding: '16px 20px',
  cursor: 'pointer'
}

const useStyles = makeStyles({
  '@keyframes barIn': {
    from: { opacity: 0, transform: 'translate(-50%, 16px)' },
    to: { opacity: 1, transform: 'translate(-50%, 0)' }
  },
  '@keyframes stateIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  statusPage: {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(180deg, #fbfcfe 0%, #eef1f7 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statusCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '40px 36px',
    margin: '0 20px',
    maxWidth: '480px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 20px 45px -20px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.04)',
    animation: '$stateIn 0.4s ease'
  },
  statusIconWrap: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f6fa',
    color: '#0e7a94',
    marginBottom: '20px'
  },
  statusIcon: {
    fontSize: '34px'
  },
  statusHeader: {
    fontWeight: '700',
    fontSize: '22px',
    color: '#0f172a'
  },
  statusSubHeader: {
    fontSize: '16px',
    marginTop: '10px',
    color: '#64748b',
    lineHeight: '1.6'
  },
  bottomBarWrapper: {
    position: 'absolute',
    bottom: '26px',
    left: '50%',
    transform: 'translateX(-50%)',
    height: '98px',
    padding: '8px 0px',
    animation: '$barIn 0.4s ease'
  },
  bottomBar: {
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(12px)',
    padding: '16px 24px',
    borderRadius: '18px',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 16px 40px -18px rgba(15, 23, 42, 0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
  start: {
    ...commonStart,
    backgroundColor: colors.indigo,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#007E99'
    }
  },
  navBtn: {
    borderRadius: '12px',
    borderColor: 'rgba(15, 23, 42, 0.15)'
  },
  arrow: {
    fontSize: '50px !important'
  },
  readyCard: {
    width: '100%',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 24px 60px -20px rgba(15, 23, 42, 0.35)',
    padding: '32px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
  },
  readyDetails: {
    display: 'flex',
    flexDirection: 'column'
  },
  readyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '18px 0',
    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
    '&:last-child': {
      paddingBottom: 0,
      borderBottom: 'none'
    },
    '&:first-child': {
      paddingTop: 0
    }
  },
  readyIconWrap: {
    flexShrink: 0,
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f6fa',
    color: '#0e7a94'
  },
  readyLabel: {
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#64748b'
  },
  readyValue: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#0f172a',
    marginTop: '2px'
  },
  readyActions: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTop: '1px solid rgba(15, 23, 42, 0.06)'
  },
  fullscreenToggle: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 500,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    boxShadow: '0 8px 20px -8px rgba(15, 23, 42, 0.35)',
    color: '#334155',
    '&:hover': {
      backgroundColor: '#FFFFFF'
    }
  },
  warningCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    padding: '24px',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 24px 60px -20px rgba(15, 23, 42, 0.35)'
  },
  boldGuide: {
    fontSize: '32px',
    fontWeight: 'bold'
  },
  cancel: {
    ...commonStart,
    width: '200px',
    color: '#757575',
    backgroundColor: '#eeeeee',
    '&:hover': {
      backgroundColor: '#bdbdbd'
    }
  },
  disabledStart: {
    ...commonStart,
    pointerEvents: 'none',
    backgroundColor: '#E0E0E0',
    color: '#BDBDBD'
  },
  startIcon: {
    fontSize: '35px !important'
  },
  backdropContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    minWidth: '560px',
    maxWidth: '800px'
  },
  bold: {
    fontWeight: 'bold'
  },
  actionBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  startBtn: {
    fontSize: '22px',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: '12px',
    padding: '8px 20px'
  },
  pauseBtn: {
    fontSize: '22px',
    backgroundColor: '#f3ad09',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: '12px',
    padding: '8px 20px',
    '&:hover': {
      backgroundColor: '#ff9a00'
    }
  },
  errorBtn: {
    fontSize: '22px',
    backgroundColor: '#d32f2f',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: '12px',
    padding: '8px 20px',
    '&:hover': {
      backgroundColor: '#c62828'
    }
  },
  questionIndex: {
    fontSize: '22px',
    letterSpacing: '1px',
    color: '#334155',
    fontWeight: '600'
  },
  exitBtn: {
    textTransform: 'none'
  },
  backdrop: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0
  },
  modalTitle: {
    fontSize: '17px'
  },
  modalText: {
    marginTop: '16px',
    fontSize: '14px'
  },
  modalActions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'end',
    gap: '4px',
    marginBottom: '4px'
  },
  btnBox: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'right',
    gap: '0.5rem'
  },
  yesBtn: {
    color: 'white'
  }
})
